import React, { useState } from "react";
import { AppShell } from "../../components/Sidebar";
import { Code2, Copy, Check, ShieldAlert } from "lucide-react";

const BASE_URL = window.location.origin;
const API_KEY_HINT = "Contact your Emergent admin — the value lives in backend/.env as VR_API_KEY and is not exposed via any API.";

const CodeBlock = ({ code, id }) => {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch (e) { /* clipboard unavailable */ }
  };
  return (
    <div className="relative">
      <pre className="max-h-[420px] overflow-auto rounded-xl bg-slate-950 p-4 pr-14 text-[13px] leading-relaxed text-slate-100 shadow-inner" data-testid={`code-${id}`}>
        <code>{code}</code>
      </pre>
      <button
        onClick={doCopy}
        data-testid={`copy-${id}`}
        className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-md bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-white ring-1 ring-white/15 hover:bg-white/20"
      >
        {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
      </button>
    </div>
  );
};

const Section = ({ n, method, path, title, children }) => (
  <section className="mt-8 rounded-2xl border border-slate-100 bg-white p-6 shadow-card" data-testid={`section-api-${n}`}>
    <div className="flex flex-wrap items-center gap-3">
      <span className="rounded-md bg-slate-900 px-2 py-1 text-xs font-mono font-bold text-white">API {n}</span>
      <span className={`rounded-md px-2 py-1 text-xs font-mono font-bold ${method === "GET" ? "bg-emerald-100 text-emerald-800" : "bg-indigo-100 text-indigo-800"}`}>{method}</span>
      <code className="text-sm font-mono text-slate-800">{path}</code>
    </div>
    <h2 className="mt-3 text-xl font-bold text-slate-900">{title}</h2>
    {children}
  </section>
);

export default function DeveloperDocs() {
  const curlGet = `curl -X GET '${BASE_URL}/api/vr/user/{code}' \\
  -H 'x-api-key: <VR_API_KEY>'`;

  const respGet = `{
  "code": "510217",
  "username": "DP8BAaravM",
  "full_name": "Aarav Mehta",
  "first_name": "Aarav",
  "last_name": "Mehta",
  "grade": "8",
  "division": "B",
  "class_label": "Class 8B",
  "school_id": "school_sunrise",
  "school_name": "Sunrise Public School",
  "roll_no": "01",
  "email": "aarav.mehta@sunrise.edu"
}`;

  const curlSession = `curl -X POST '${BASE_URL}/api/vr/session' \\
  -H 'x-api-key: <VR_API_KEY>' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "code": "510217",
    "Date": "2026-02-14",
    "Username": "DP8BAaravM",
    "DeviceID": "HEADSET-001",
    "DeviceName": "Meta Quest 3",
    "SessionID": "sess-xyz-2026-02-14-10-15",
    "RoomName": "Physics Lab",
    "ModuleName": "Force and Pressure",
    "StartTime": "2026-02-14T10:15:00Z",
    "EndTime":   "2026-02-14T10:35:00Z",
    "DurationSeconds": 1200
  }'`;

  const respSession = `{ "ok": true, "id": "cd50e45c-3017-446c-bf0d-bbf145863c91" }`;

  const curlQuiz = `curl -X POST '${BASE_URL}/api/vr/quiz' \\
  -H 'x-api-key: <VR_API_KEY>' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "code": "510217",
    "Date": "2026-02-14",
    "Time": "10:35",
    "Username": "DP8BAaravM",
    "RoomName": "Physics Lab",
    "TotalQuestions": 10,
    "QuestionsAnswered": 10,
    "CorrectAnswers": 8,
    "Score": 80
  }'`;

  const respQuiz = `{ "ok": true, "id": "5f9bbc39-ee9d-4a3e-a908-cfe718cc4644" }`;

  const errUnauth = `HTTP 401 Unauthorized
{ "detail": "Invalid or missing x-api-key" }`;
  const errNotFound = `HTTP 404 Not Found
{ "detail": { "error": "Invalid code" } }`;
  const errValidation = `HTTP 422 Unprocessable Entity
{ "detail": [ { "loc": ["body","code"], "msg": "field required", "type": "value_error.missing" } ] }`;

  return (
    <AppShell role="admin">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white"><Code2 size={20} /></div>
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">VR Developer Docs</h1>
          <p className="mt-1 text-slate-500">Reference for the three APIs consumed by the Unity VR headset.</p>
        </div>
      </div>

      {/* Base info + Auth */}
      <section className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <h2 className="text-lg font-bold text-slate-900">Base URL &amp; Authentication</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Base URL</p>
            <p className="mt-1 font-mono text-sm text-slate-900" data-testid="doc-base-url">{BASE_URL}</p>
          </div>
          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Auth Header</p>
            <p className="mt-1 font-mono text-sm text-slate-900">x-api-key: &lt;VR_API_KEY&gt;</p>
            <p className="mt-2 text-xs text-slate-500">{API_KEY_HINT}</p>
          </div>
        </div>
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-900">
          <ShieldAlert size={16} className="mt-0.5 flex-none" />
          <p>The <code className="font-mono text-xs">code</code> path/body parameter is the student&apos;s permanent 6-digit VR Login Code, shown on the Student Dashboard. It never changes and never expires.</p>
        </div>
      </section>

      {/* API 2 - GET user */}
      <Section n="1" method="GET" path="/api/vr/user/{code}" title="Fetch User Info by VR Code">
        <p className="mt-2 text-sm text-slate-600">Returns the student profile bound to a permanent VR code.</p>
        <h3 className="mt-4 text-sm font-semibold text-slate-700">Sample Request</h3>
        <div className="mt-2"><CodeBlock code={curlGet} id="get-user-req" /></div>
        <h3 className="mt-4 text-sm font-semibold text-slate-700">Sample Response — HTTP 200</h3>
        <div className="mt-2"><CodeBlock code={respGet} id="get-user-resp" /></div>
      </Section>

      {/* API 3 - POST session */}
      <Section n="2" method="POST" path="/api/vr/session" title="Submit Session Analytics">
        <p className="mt-2 text-sm text-slate-600">Records a completed VR session. Call once per session (or when the headset finishes/exits a room).</p>
        <h3 className="mt-4 text-sm font-semibold text-slate-700">Sample Request</h3>
        <div className="mt-2"><CodeBlock code={curlSession} id="post-session-req" /></div>
        <h3 className="mt-4 text-sm font-semibold text-slate-700">Sample Response — HTTP 200</h3>
        <div className="mt-2"><CodeBlock code={respSession} id="post-session-resp" /></div>
      </Section>

      {/* API 4 - POST quiz */}
      <Section n="3" method="POST" path="/api/vr/quiz" title="Submit Quiz Results">
        <p className="mt-2 text-sm text-slate-600">Records a completed VR quiz attempt. Call once per attempt.</p>
        <h3 className="mt-4 text-sm font-semibold text-slate-700">Sample Request</h3>
        <div className="mt-2"><CodeBlock code={curlQuiz} id="post-quiz-req" /></div>
        <h3 className="mt-4 text-sm font-semibold text-slate-700">Sample Response — HTTP 200</h3>
        <div className="mt-2"><CodeBlock code={respQuiz} id="post-quiz-resp" /></div>
      </Section>

      {/* Errors */}
      <section className="mt-8 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <h2 className="text-xl font-bold text-slate-900">Error Response Formats</h2>
        <p className="mt-2 text-sm text-slate-600">All errors return standard HTTP status codes with a JSON body.</p>
        <div className="mt-4 space-y-4">
          <div>
            <p className="text-sm font-semibold text-slate-700">Missing / invalid <code className="font-mono text-xs">x-api-key</code></p>
            <div className="mt-1"><CodeBlock code={errUnauth} id="err-unauth" /></div>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700">Unknown VR code</p>
            <div className="mt-1"><CodeBlock code={errNotFound} id="err-not-found" /></div>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700">Missing required fields</p>
            <div className="mt-1"><CodeBlock code={errValidation} id="err-validation" /></div>
          </div>
        </div>
      </section>
    </AppShell>
  );
}
